import React from 'react';
import { X, Table, MessageSquare, CheckCircle2 } from 'lucide-react';

const FormatSelectionModal = ({ isOpen, onClose, onSelect, currentFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <X size={20} />
                </button>

                <div className="p-8">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Output Format</h2>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-amber-700 text-xs font-semibold uppercase tracking-wider">
                            <CheckCircle2 size={12} className="text-amber-500" />
                            Supports up to 500 rows per generation
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Tabular Format Card */}
                        <div
                            className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 group ${currentFormat === 'Flat_Tabular' ? 'border-blue-500 bg-blue-50/30' : 'border-gray-200 hover:border-blue-300 hover:shadow-md'}`}
                            onClick={() => onSelect('Flat_Tabular')}
                        >
                            {currentFormat === 'Flat_Tabular' && (
                                <div className="absolute top-4 right-4 text-blue-500">
                                    <CheckCircle2 size={24} className="fill-blue-100" />
                                </div>
                            )}
                            <div className="flex items-center gap-3 mb-4">
                                <div className={`p-3 rounded-lg ${currentFormat === 'Flat_Tabular' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 group-hover:bg-blue-50 group-hover:text-blue-500'}`}>
                                    <Table size={24} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Structured</h3>
                                    <p className="text-sm text-gray-500">CSV, Excel or JSON</p>
                                </div>
                            </div>

                            {/* Example Visual */}
                            <div className="bg-white border border-gray-200 rounded-lg p-3 mt-4 shadow-sm">
                                <div className="flex border-b border-gray-100 pb-2 mb-2 text-[10px] font-semibold text-gray-500 bg-gray-50/50 p-1 rounded-t-lg">
                                    <div className="w-1/3 px-2">Instruction</div>
                                    <div className="w-2/3 px-2">Output</div>
                                </div>
                                <div className="flex text-[10px] text-gray-700 py-1.5 border-b border-gray-50">
                                    <div className="w-1/3 px-2 truncate">Write a poem</div>
                                    <div className="w-2/3 px-2 truncate">The sun sets low...</div>
                                </div>
                                <div className="flex text-[10px] text-gray-700 py-1.5 shadow-sm bg-blue-50/20">
                                    <div className="w-1/3 px-2 truncate font-medium">Explain gravity</div>
                                    <div className="w-2/3 px-2 truncate">A force that pulls...</div>
                                </div>

                                <div className="relative my-3 flex items-center">
                                    <div className="flex-grow border-t border-gray-100"></div>
                                    <span className="flex-shrink mx-3 text-[9px] font-bold text-gray-400 tracking-widest">OR</span>
                                    <div className="flex-grow border-t border-gray-100"></div>
                                </div>

                                <div className="font-mono text-[9px] text-gray-500 bg-gray-50 p-2 rounded leading-relaxed">
                                    <span className="text-blue-500">[</span><br />
                                    &nbsp;&nbsp;{`{ "instruction": "...", "output": "..." }`},<br />
                                    &nbsp;&nbsp;{`{ "instruction": "...", "output": "..." }`}<br />
                                    <span className="text-blue-500">]</span>
                                </div>
                            </div>

                            <button className={`mt-6 w-full py-2.5 rounded-lg font-medium transition-colors ${currentFormat === 'Flat_Tabular' ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                {currentFormat === 'Flat_Tabular' ? 'Continue with Structured' : 'Select Structured'}
                            </button>
                        </div>

                        {/* Chat Data Format Card */}
                        <div
                            className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 group ${currentFormat === 'chat_array' ? 'border-indigo-500 bg-indigo-50/30' : 'border-gray-200 hover:border-indigo-300 hover:shadow-md'}`}
                            onClick={() => onSelect('chat_array')}
                        >
                            {currentFormat === 'chat_array' && (
                                <div className="absolute top-4 right-4 text-indigo-500">
                                    <CheckCircle2 size={24} className="fill-indigo-100" />
                                </div>
                            )}
                            <div className="flex items-center gap-3 mb-4">
                                <div className={`p-3 rounded-lg ${currentFormat === 'chat_array' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600 group-hover:bg-indigo-50 group-hover:text-indigo-500'}`}>
                                    <MessageSquare size={24} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Chat Data</h3>
                                    <p className="text-sm text-gray-500">Conversational AI format</p>
                                </div>
                            </div>

                            {/* Example Visual */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4 mt-6 shadow-sm space-y-3">
                                <div className="flex flex-col items-end">
                                    <span className="text-[10px] text-gray-400 font-medium mb-1 mr-1">User</span>
                                    <div className="bg-blue-600 text-white text-xs p-2 rounded-lg rounded-tr-sm max-w-[85%] shadow-sm">
                                        "How do I reset my password?"
                                    </div>
                                </div>
                                <div className="flex flex-col items-start">
                                    <span className="text-[10px] text-gray-400 font-medium mb-1 ml-1">Assistant</span>
                                    <div className="bg-gray-100 text-gray-800 text-xs p-2 rounded-lg rounded-tl-sm max-w-[85%] shadow-sm whitespace-pre-line">
                                        "To reset your password, go to settings &gt; security."
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-100 font-mono text-[10px] text-gray-500 bg-gray-50 p-2 rounded">
                                    {`{"messages": [{"role": "user", ...}]}`}
                                </div>
                            </div>

                            <button className={`mt-6 w-full py-2.5 rounded-lg font-medium transition-colors ${currentFormat === 'chat_array' ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                {currentFormat === 'chat_array' ? 'Continue with Chat Data' : 'Select Chat Data'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormatSelectionModal;
